// src/pages/admin/Raffle.tsx
// Admin-only raffle console (issue #16). Routed at /admin/raffle behind
// AdminGuard. Backed by /api/manage/raffle{,/assign,/exclude/{id},/config}.
//
// One place for everything raffle: ticket-number range, assignment (fill-new or
// reshuffle), the number→holder draw lookup, prize list (hidden until revealed,
// winner recorded per prize), and per-employee exclusion checkboxes.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";

const YEAR = "2026";

interface Prize {
  slug: string;
  name: string;
  description?: string;
  sponsor?: string;
  revealed: boolean;
  winnerTicket?: number;
  winnerName?: string;
}
interface RaffleConfig {
  enabled: boolean;
  description?: string;
  ticketPrefix?: string;
  ticketRangeStart: number;
  ticketRangeEnd: number;
  assignedAt?: string;
  prizes: Prize[];
}

/** Format a numeric ticket suffix for display. With a book/series prefix it
 *  renders as #####-NNN (issue #35); without one it falls back to #NNN. */
function fmtTicket(suffix: number, prefix?: string): string {
  const s = String(suffix).padStart(3, "0");
  return prefix ? `${prefix}-${s}` : `#${s}`;
}

/** Extract the numeric suffix from a draw entry. Accepts the full "01234-567"
 *  form, a bare suffix ("567"), or a prefix-glued string ("01234567"). */
function parseDrawSuffix(input: string, prefix?: string): number {
  let s = input.trim();
  if (s.includes("-")) {
    s = s.slice(s.lastIndexOf("-") + 1);
  } else if (prefix && s.length > prefix.length && s.startsWith(prefix)) {
    s = s.slice(prefix.length);
  }
  const n = parseInt(s, 10);
  return Number.isInteger(n) ? n : NaN;
}
interface TicketHolder {
  ticket: number;
  displayName: string;
  email?: string;
  upn?: string;
}
interface EmployeeRow {
  id: string;
  displayName: string;
  email?: string;
  upn?: string;
  raffleExcluded: boolean;
  raffleTicket: number | null;
}
interface Status {
  config: RaffleConfig | null;
  eligibleCount: number;
  excludedCount: number;
  assignedCount: number;
  tickets: TicketHolder[];
  employees: EmployeeRow[];
}

function fmtDateTime(iso?: string) {
  if (!iso) return "never";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export default function Raffle() {
  const { getApiToken } = useAuth();

  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Range editor
  const [prefix, setPrefix] = useState("");
  const [rangeStart, setRangeStart] = useState("1");
  const [rangeEnd, setRangeEnd] = useState("100");

  // Prize editor (local working copy)
  const [prizes, setPrizes] = useState<Prize[]>([]);

  // Draw
  const [drawInput, setDrawInput] = useState("");
  const [drawResult, setDrawResult] = useState<TicketHolder | "none" | null>(null);
  const [attachSlug, setAttachSlug] = useState("");

  const load = useCallback(async () => {
    setErr(null);
    try {
      const token = await getApiToken();
      const res = await fetch(`/api/manage/raffle?year=${YEAR}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const data: Status = await res.json();
      setStatus(data);
      if (data.config) {
        setPrefix(data.config.ticketPrefix ?? "");
        setRangeStart(String(data.config.ticketRangeStart));
        setRangeEnd(String(data.config.ticketRangeEnd));
        setPrizes(data.config.prizes ?? []);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load raffle status.");
    } finally {
      setLoading(false);
    }
  }, [getApiToken]);

  useEffect(() => { load(); }, [load]);

  async function api(path: string, method: string, body?: unknown): Promise<Response> {
    const token = await getApiToken();
    return fetch(`/api/manage/raffle${path}${path.includes("?") ? "&" : "?"}year=${YEAR}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  async function saveRange() {
    setBusy("range"); setErr(null);
    try {
      const res = await api("/config", "PUT", {
        ticketPrefix: prefix.trim(),
        ticketRangeStart: parseInt(rangeStart, 10),
        ticketRangeEnd: parseInt(rangeEnd, 10),
      });
      if (!res.ok) { setErr((await res.json().catch(() => ({}))).message ?? "Save failed."); return; }
      setStatus(await res.json());
    } finally { setBusy(null); }
  }

  async function assign(reshuffleAll: boolean) {
    if (reshuffleAll && !confirm("Reshuffle ALL tickets? Every employee gets a new number — anyone who already saw theirs will see it change.")) return;
    setBusy("assign"); setErr(null);
    try {
      const res = await api("/assign", "POST", { reshuffleAll });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(data.message ?? "Assignment failed."); return; }
      setStatus(data);
    } finally { setBusy(null); }
  }

  async function clearTickets() {
    if (!confirm("Clear ALL assigned ticket numbers? Everyone loses their number. You can re-assign afterwards.")) return;
    setBusy("clear"); setErr(null);
    try {
      const res = await api("/clear", "POST");
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(data.message ?? "Clear failed."); return; }
      setStatus(data);
    } finally { setBusy(null); }
  }

  async function toggleExclude(emp: EmployeeRow) {
    setBusy(`ex:${emp.id}`); setErr(null);
    try {
      const res = await api(`/exclude/${encodeURIComponent(emp.id)}`, "PUT", { excluded: !emp.raffleExcluded });
      if (!res.ok) { setErr((await res.json().catch(() => ({}))).message ?? "Update failed."); return; }
      await load();
    } finally { setBusy(null); }
  }

  async function savePrizes(next: Prize[]) {
    setBusy("prizes"); setErr(null);
    try {
      const res = await api("/config", "PUT", { prizes: next });
      if (!res.ok) { setErr((await res.json().catch(() => ({}))).message ?? "Save failed."); return; }
      const data: Status = await res.json();
      setStatus(data);
      setPrizes(data.config?.prizes ?? []);
    } finally { setBusy(null); }
  }

  function runDraw() {
    const n = parseDrawSuffix(drawInput, cfg?.ticketPrefix);
    if (!Number.isInteger(n)) { setDrawResult(null); return; }
    const hit = status?.tickets.find(t => t.ticket === n);
    setDrawResult(hit ?? "none");
  }

  function recordWinner() {
    if (drawResult === null || drawResult === "none" || !attachSlug) return;
    const next = prizes.map(p =>
      p.slug === attachSlug
        ? { ...p, winnerTicket: drawResult.ticket, winnerName: drawResult.displayName, revealed: true }
        : p);
    savePrizes(next);
  }

  // Local prize edits
  function addPrize() {
    setPrizes(prev => [...prev, { slug: `prize-${Date.now()}-${prev.length}`, name: "", revealed: false }]);
  }
  function updatePrize(slug: string, patch: Partial<Prize>) {
    setPrizes(prev => prev.map(p => p.slug === slug ? { ...p, ...patch } : p));
  }
  function removePrize(slug: string) {
    setPrizes(prev => prev.filter(p => p.slug !== slug));
  }

  const cfg = status?.config;

  return (
    <>
      <style>{`
        .rf-page { min-height: 100vh; background: var(--bg); padding-bottom: 5rem; }
        .rf-header { background: var(--black); color: var(--white); padding: 3rem 4rem 2.5rem; border-bottom: 3px solid var(--red); }
        .rf-header-inner { max-width: 1000px; margin: 0 auto; }
        .eyebrow { font-family: var(--font-display); font-size: 0.72rem; font-weight: 500; letter-spacing: 0.35em; text-transform: uppercase; color: var(--red); margin-bottom: 0.5rem; }
        .rf-header h1 { font-family: var(--font-display); font-size: clamp(1.6rem, 3.5vw, 2.4rem); font-weight: 700; text-transform: uppercase; margin: 0; }
        .rf-back { font-family: var(--font-display); font-size: 0.72rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: rgba(255,255,255,0.7); text-decoration: none; }
        .rf-back:hover { color: var(--white); }
        .rf-wrap { max-width: 1000px; margin: 0 auto; padding: 2.5rem 4rem; }
        @media (max-width: 768px) { .rf-header { padding: 2.5rem 1.5rem 2rem; } .rf-wrap { padding: 2rem 1.5rem; } }
        .rf-section { background: var(--white); border: 1px solid var(--border); padding: 1.75rem; margin-bottom: 1.5rem; }
        .rf-section h2 { font-family: var(--font-display); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--black); margin: 0 0 0.35rem; }
        .rf-section p.sub { font-family: var(--font-body); font-size: 0.85rem; color: var(--muted); margin: 0 0 1.25rem; }
        .rf-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 1px; background: var(--border); border: 1px solid var(--border); margin-bottom: 1.5rem; }
        .rf-stat { background: var(--white); padding: 1.25rem 1.5rem; }
        .rf-stat .l { font-family: var(--font-display); font-size: 0.62rem; font-weight: 500; letter-spacing: 0.18em; text-transform: uppercase; color: var(--muted); }
        .rf-stat .v { font-family: var(--font-display); font-size: 1.9rem; font-weight: 700; color: var(--black); line-height: 1.1; }
        .rf-stat .v.red { color: var(--red); }
        .rf-row { display: flex; gap: 1rem; align-items: flex-end; flex-wrap: wrap; }
        .rf-field { display: flex; flex-direction: column; gap: 0.35rem; }
        .rf-label { font-family: var(--font-display); font-size: 0.62rem; font-weight: 600; letter-spacing: 0.15em; text-transform: uppercase; color: var(--muted); }
        .rf-input { font-family: var(--font-body); font-size: 0.95rem; padding: 0.55rem 0.7rem; border: 1px solid var(--border); background: var(--white); color: var(--black); width: 120px; }
        .rf-input.wide { width: 100%; box-sizing: border-box; }
        .rf-input:focus { outline: none; border-color: var(--red); }
        .rf-btn { font-family: var(--font-display); font-size: 0.74rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--white); background: var(--red); border: none; padding: 0.6rem 1.4rem; cursor: pointer; transition: background 0.15s; }
        .rf-btn:hover:not(:disabled) { background: var(--red-dark); }
        .rf-btn:disabled { background: var(--muted); cursor: not-allowed; }
        .rf-btn.ghost { background: var(--white); color: var(--black); border: 1px solid var(--border); }
        .rf-btn.ghost:hover:not(:disabled) { background: var(--black); color: var(--white); border-color: var(--black); }
        .rf-btn.ghost.danger { color: var(--red); border-color: var(--red); }
        .rf-btn.ghost.danger:hover:not(:disabled) { background: var(--red); color: var(--white); border-color: var(--red); }
        .rf-err { background: #fdecea; color: #b71c1c; font-family: var(--font-body); font-size: 0.85rem; padding: 0.6rem 0.85rem; margin-bottom: 1rem; }
        .rf-note { font-family: var(--font-body); font-size: 0.82rem; color: var(--muted); margin-top: 0.85rem; }
        table { width: 100%; border-collapse: collapse; font-family: var(--font-body); font-size: 0.88rem; }
        thead th { font-family: var(--font-display); font-size: 0.62rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.12em; color: var(--muted); text-align: left; padding: 0.5rem 0.6rem; border-bottom: 2px solid var(--border); }
        tbody td { padding: 0.55rem 0.6rem; color: var(--steel); border-bottom: 1px solid var(--border); vertical-align: middle; }
        .rf-num { font-family: var(--font-display); font-weight: 700; color: var(--black); font-variant-numeric: tabular-nums; }
        .rf-draw-result { margin-top: 1rem; padding: 1rem 1.25rem; border: 1px solid var(--border); background: var(--bg); }
        .rf-draw-result.hit { border-color: var(--red); }
        .rf-draw-name { font-family: var(--font-display); font-size: 1.3rem; font-weight: 700; text-transform: uppercase; color: var(--red); margin: 0; }
        .rf-draw-email { font-family: var(--font-body); font-size: 0.85rem; color: var(--muted); margin: 0.15rem 0 0; }
        .rf-prize { border: 1px solid var(--border); padding: 1rem 1.1rem; margin-bottom: 0.85rem; }
        .rf-prize-top { display: flex; gap: 0.6rem; align-items: center; flex-wrap: wrap; }
        .rf-prize-top .rf-input { flex: 1; min-width: 160px; width: auto; }
        .rf-check { display: inline-flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 0.85rem; color: var(--steel); cursor: pointer; white-space: nowrap; }
        .rf-check input { accent-color: var(--red); width: 1.05rem; height: 1.05rem; }
        .rf-link-btn { font-family: var(--font-display); font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; background: none; border: none; color: var(--red); cursor: pointer; padding: 0; }
        .rf-winner { font-family: var(--font-body); font-size: 0.82rem; color: #2e7d32; margin-top: 0.5rem; }
        .rf-select { font-family: var(--font-body); font-size: 0.9rem; padding: 0.5rem 0.6rem; border: 1px solid var(--border); background: var(--white); }
      `}</style>

      <div className="rf-page">
        <div className="rf-header">
          <div className="rf-header-inner">
            <p className="eyebrow">— Raffle Console</p>
            <h1>WICnic Raffle</h1>
            <div style={{ marginTop: "1rem" }}>
              <Link to="/admin" className="rf-back">← Back to dashboard</Link>
            </div>
          </div>
        </div>

        <div className="rf-wrap">
          {err && <div className="rf-err">{err}</div>}

          {loading ? (
            <div className="rf-section"><p className="sub" style={{ margin: 0 }}>Loading…</p></div>
          ) : (
            <>
              {/* Stats */}
              <div className="rf-stats">
                <div className="rf-stat"><div className="l">Eligible</div><div className="v red">{status?.eligibleCount ?? 0}</div></div>
                <div className="rf-stat"><div className="l">Assigned</div><div className="v">{status?.assignedCount ?? 0}</div></div>
                <div className="rf-stat"><div className="l">Excluded</div><div className="v">{status?.excludedCount ?? 0}</div></div>
                <div className="rf-stat"><div className="l">Range</div><div className="v" style={{ fontSize: "1.2rem" }}>{cfg ? `${fmtTicket(cfg.ticketRangeStart, cfg.ticketPrefix)} – ${fmtTicket(cfg.ticketRangeEnd, cfg.ticketPrefix)}` : "—"}</div></div>
              </div>

              {/* Range + assign */}
              <div className="rf-section">
                <h2>Ticket Numbers</h2>
                <p className="sub">
                  One ticket per employee who's RSVP'd "yes" (excluding anyone opted out below).
                  Set the range, then assign. Add an optional <strong>book prefix</strong> for
                  physical-ticket style numbers — tickets then read as {prefix.trim() ? `${prefix.trim()}-NNN` : "#####-NNN"}.
                  Last assigned: {fmtDateTime(cfg?.assignedAt)}.
                </p>
                <div className="rf-row">
                  <div className="rf-field">
                    <span className="rf-label">Book prefix (optional)</span>
                    <input className="rf-input" type="text" inputMode="numeric" placeholder="e.g. 01234"
                      value={prefix} onChange={e => setPrefix(e.target.value)} />
                  </div>
                  <div className="rf-field">
                    <span className="rf-label">Range start</span>
                    <input className="rf-input" type="number" min={0} value={rangeStart} onChange={e => setRangeStart(e.target.value)} />
                  </div>
                  <div className="rf-field">
                    <span className="rf-label">Range end</span>
                    <input className="rf-input" type="number" min={0} value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} />
                  </div>
                  <button className="rf-btn ghost" onClick={saveRange} disabled={busy !== null}>
                    {busy === "range" ? "Saving…" : "Save range"}
                  </button>
                </div>
                <div className="rf-row" style={{ marginTop: "1.25rem" }}>
                  <button className="rf-btn" onClick={() => assign(false)} disabled={busy !== null || !cfg}>
                    {busy === "assign" ? "Working…" : "Assign tickets"}
                  </button>
                  <button className="rf-btn ghost" onClick={() => assign(true)} disabled={busy !== null || !cfg}>
                    Reshuffle all
                  </button>
                  <button className="rf-btn ghost danger" onClick={clearTickets} disabled={busy !== null || (status?.assignedCount ?? 0) === 0}>
                    {busy === "clear" ? "Clearing…" : "Clear all tickets"}
                  </button>
                </div>
                <p className="rf-note">
                  <strong>Assign tickets</strong> keeps existing numbers and only fills newly-eligible employees.
                  <strong> Reshuffle all</strong> discards every number and redraws.
                  <strong> Clear all tickets</strong> removes every number without re-assigning.
                </p>
              </div>

              {/* Draw */}
              <div className="rf-section">
                <h2>Draw</h2>
                <p className="sub">
                  Type a winning number to see who holds it.
                  {cfg?.ticketPrefix ? ` Enter the full ticket (${cfg.ticketPrefix}-NNN) or just the last digits.` : ""}
                </p>
                <div className="rf-row">
                  <div className="rf-field">
                    <span className="rf-label">Winning number</span>
                    <input className="rf-input" type="text" inputMode="numeric" value={drawInput}
                      placeholder={cfg?.ticketPrefix ? `${cfg.ticketPrefix}-007` : "e.g. 7"}
                      onChange={e => { setDrawInput(e.target.value); setDrawResult(null); }}
                      onKeyDown={e => { if (e.key === "Enter") runDraw(); }} />
                  </div>
                  <button className="rf-btn" onClick={runDraw} disabled={!drawInput}>Look up</button>
                </div>

                {drawResult === "none" && (
                  <div className="rf-draw-result">
                    <p style={{ margin: 0, color: "var(--muted)", fontFamily: "var(--font-body)" }}>
                      No one holds ticket {drawInput}. Try another number.
                    </p>
                  </div>
                )}
                {drawResult && drawResult !== "none" && (
                  <div className="rf-draw-result hit">
                    <p className="rf-draw-name">🎟 {drawResult.displayName}</p>
                    <p className="rf-draw-email">Ticket {fmtTicket(drawResult.ticket, cfg?.ticketPrefix)}{drawResult.email ? ` · ${drawResult.email}` : ""}</p>
                    {prizes.length > 0 && (
                      <div className="rf-row" style={{ marginTop: "0.85rem" }}>
                        <select className="rf-select" value={attachSlug} onChange={e => setAttachSlug(e.target.value)}>
                          <option value="">Attach to prize…</option>
                          {prizes.map(p => <option key={p.slug} value={p.slug}>{p.name || "(unnamed prize)"}</option>)}
                        </select>
                        <button className="rf-btn" onClick={recordWinner} disabled={!attachSlug || busy !== null}>
                          Record winner
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Prizes */}
              <div className="rf-section">
                <h2>Prizes</h2>
                <p className="sub">Listed but hidden from the public until you reveal each one. Winners are recorded from the draw above.</p>
                {prizes.map(p => (
                  <div className="rf-prize" key={p.slug}>
                    <div className="rf-prize-top">
                      <input className="rf-input" placeholder="Prize name" value={p.name}
                        onChange={e => updatePrize(p.slug, { name: e.target.value })} />
                      <input className="rf-input" placeholder="Sponsor (optional)" value={p.sponsor ?? ""}
                        onChange={e => updatePrize(p.slug, { sponsor: e.target.value })} />
                      <label className="rf-check">
                        <input type="checkbox" checked={p.revealed} onChange={e => updatePrize(p.slug, { revealed: e.target.checked })} />
                        Revealed
                      </label>
                      <button className="rf-link-btn" onClick={() => removePrize(p.slug)}>Remove</button>
                    </div>
                    <input className="rf-input wide" style={{ marginTop: "0.6rem" }} placeholder="Description (optional)"
                      value={p.description ?? ""} onChange={e => updatePrize(p.slug, { description: e.target.value })} />
                    {typeof p.winnerTicket === "number" && (
                      <p className="rf-winner">🏆 Winner: {fmtTicket(p.winnerTicket, cfg?.ticketPrefix)} — {p.winnerName ?? "—"}</p>
                    )}
                  </div>
                ))}
                <div className="rf-row" style={{ marginTop: "0.5rem" }}>
                  <button className="rf-btn ghost" onClick={addPrize} disabled={busy !== null}>+ Add prize</button>
                  <button className="rf-btn" onClick={() => savePrizes(prizes)} disabled={busy !== null}>
                    {busy === "prizes" ? "Saving…" : "Save prizes"}
                  </button>
                </div>
              </div>

              {/* Exclusions */}
              <div className="rf-section">
                <h2>Employees</h2>
                <p className="sub">Attending employees. Check "Exclude" to leave someone out of ticket assignment (clears any number they hold).</p>
                {status && status.employees.length > 0 ? (
                  <table>
                    <thead>
                      <tr><th>Name</th><th>Email</th><th>Ticket</th><th style={{ textAlign: "center" }}>Exclude</th></tr>
                    </thead>
                    <tbody>
                      {status.employees.map(emp => (
                        <tr key={emp.id}>
                          <td style={{ color: "var(--black)", fontWeight: 500 }}>{emp.displayName}</td>
                          <td>{emp.email ?? emp.upn ?? "—"}</td>
                          <td className="rf-num">{emp.raffleTicket !== null ? fmtTicket(emp.raffleTicket, cfg?.ticketPrefix) : "—"}</td>
                          <td style={{ textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={emp.raffleExcluded}
                              disabled={busy !== null}
                              onChange={() => toggleExclude(emp)}
                              aria-label={`Exclude ${emp.displayName} from the raffle`}
                              style={{ accentColor: "var(--red)", width: "1.1rem", height: "1.1rem", cursor: "pointer" }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="sub" style={{ margin: 0 }}>No attending employees yet.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
